import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config';
import { IconAlert, IconMessageSquare } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import './AdminFeedback.css';

const AUTH_TOKEN_KEY = 'risklo_auth_token';
const STATUS_OPTIONS = ['active', 'complete', 'ignored', 'deleted'];
const MESSAGE_PREVIEW_LEN = 50;

function truncate(str, len) {
  if (!str || typeof str !== 'string') return '';
  const s = str.trim();
  if (s.length <= len) return s;
  return s.slice(0, len) + '…';
}

function AdminFeedback({ onNavigate }) {
  const { isAdmin } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('complete');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [statusUpdates, setStatusUpdates] = useState({});
  const [adminNotes, setAdminNotes] = useState({});
  const [saving, setSaving] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [metadataCoverage, setMetadataCoverage] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState('');

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const params = { limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await fetch(API_ENDPOINTS.adminFeedback(params), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load feedback');
      }
      setFeedback(data.feedback || []);
      setTotal(data.total ?? 0);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const fetchMetadataCoverage = useCallback(async () => {
    if (!isAdmin) return;
    setMetadataLoading(true);
    setMetadataError('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(API_ENDPOINTS.adminStrategyMetadataValidate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load strategy metadata coverage');
      }
      setMetadataCoverage(data);
    } catch (err) {
      setMetadataError(err.message || 'Failed to load strategy metadata coverage');
      setMetadataCoverage(null);
    } finally {
      setMetadataLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchMetadataCoverage();
  }, [fetchMetadataCoverage]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const ids = displayFeedback.map((f) => f.id);
    if (selectedIds.size === ids.length && ids.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(ids));
    }
  };

  const handleBulkStatus = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkApplying(true);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(API_ENDPOINTS.adminFeedbackBulk, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids, status: bulkStatus }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to update');
      }
      setSelectedIds(new Set());
      fetchFeedback();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkApplying(false);
    }
  };

  const handleSave = async (id) => {
    const notes = adminNotes[id];
    const status = statusUpdates[id];
    if (notes === undefined && !status) return true;
    setSaving(id);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const body = {};
      if (notes !== undefined) body.admin_notes = notes;
      if (status) body.status = status;
      const res = await fetch(API_ENDPOINTS.adminFeedbackUpdate(id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to update');
      }
      setStatusUpdates((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      fetchFeedback();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#3b82f6';
      case 'complete': return '#22c55e';
      case 'ignored': return '#6b7280';
      case 'deleted': return '#ef4444';
      default: return '#6b7280';
    }
  };


  const searchLower = searchTerm.trim().toLowerCase();
  const filteredFeedback = searchLower
    ? feedback.filter(
        (f) =>
          String(f.title || '').toLowerCase().includes(searchLower) ||
          String(f.message || '').toLowerCase().includes(searchLower) ||
          String(f.user_name || '').toLowerCase().includes(searchLower) ||
          String(f.user_email || '').toLowerCase().includes(searchLower) ||
          String(f.admin_notes || '').toLowerCase().includes(searchLower)
      )
    : feedback;

  const sortedFeedback = [...filteredFeedback].sort((a, b) => {
    let va, vb;
    switch (sortBy) {
      case 'id':
        va = a.id;
        vb = b.id;
        return sortDir === 'asc' ? (va - vb) : (vb - va);
      case 'title':
        va = (a.title || '').toLowerCase();
        vb = (b.title || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      case 'submitter':
        va = (a.user_name || a.user_email || '').toLowerCase();
        vb = (b.user_name || b.user_email || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      case 'email':
        va = (a.user_email || '').toLowerCase();
        vb = (b.user_email || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      case 'date':
        va = new Date(a.created_at).getTime();
        vb = new Date(b.created_at).getTime();
        return sortDir === 'asc' ? va - vb : vb - va;
      case 'status':
        va = (a.status || 'active').toLowerCase();
        vb = (b.status || 'active').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      case 'message':
        va = (a.message || '').toLowerCase();
        vb = (b.message || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      default:
        return 0;
    }
  });

  const displayFeedback = sortedFeedback;
  const allSelectedFiltered = displayFeedback.length > 0 && selectedIds.size === displayFeedback.length;

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir(col === 'date' || col === 'id' ? 'desc' : 'asc');
    }
  };

  const handleRowClick = (e, item) => {
    if (e.target.closest('input, select, button')) return;
    setDetailItem(item);
  };

  return (
    <div className="admin-feedback-page">
      <div className="admin-feedback-content">
        <div className="admin-feedback-header">
          <h1 className="admin-feedback-title">
            <IconMessageSquare size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
            Feedback Admin
          </h1>
          <div className="admin-feedback-filters">
            <input
              type="text"
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-feedback-search"
            />
            <label>
              Status:{' '}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-feedback-select"
              >
                <option value="">All</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {isAdmin && (
          <div className="admin-metadata-panel">
            <div className="admin-metadata-panel-header">
              <h2 className="admin-metadata-panel-title">Strategy Metadata Coverage</h2>
              <button
                type="button"
                className="admin-metadata-refresh-btn"
                onClick={fetchMetadataCoverage}
                disabled={metadataLoading}
              >
                {metadataLoading ? 'Checking...' : 'Refresh'}
              </button>
            </div>

            {metadataError && (
              <div className="admin-metadata-error">
                <IconAlert size={16} />
                <span>{metadataError}</span>
              </div>
            )}

            {metadataCoverage && (
              <div className="admin-metadata-summary">
                <div>
                  <strong>{metadataCoverage.checkedCount || 0}</strong> strategies checked
                </div>
                <div>
                  <strong>{metadataCoverage.unmatched?.length || 0}</strong> unmatched
                </div>
                <div className="admin-metadata-path" title={metadataCoverage.metadataPath || ''}>
                  Source: {metadataCoverage.metadataPath || 'N/A'}
                </div>
                {(metadataCoverage.unmatched?.length || 0) > 0 && (
                  <div className="admin-metadata-unmatched">
                    <strong>Unmatched:</strong> {metadataCoverage.unmatched.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="admin-feedback-error">
            <IconAlert size={18} />
            {error}
          </div>
        )}

        {loading ? (
          <p className="admin-feedback-loading">Loading feedback...</p>
        ) : feedback.length === 0 ? (
          <p className="admin-feedback-empty">No feedback yet.</p>
        ) : (
          <>
            {selectedIds.size > 0 && (
              <div className="admin-feedback-bulk-bar">
                <div className="admin-feedback-bulk-left">
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => setSelectedIds(new Set())}
                    className="admin-feedback-checkbox"
                    aria-label="Clear selection"
                  />
                  <span className="admin-feedback-bulk-count">{selectedIds.size} selected</span>
                </div>
                <div className="admin-feedback-bulk-actions">
                  <select
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="admin-feedback-select admin-feedback-bulk-select"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>Mark as {s}</option>
                    ))}
                  </select>
                  <button
                    className="admin-feedback-bulk-btn"
                    onClick={handleBulkStatus}
                    disabled={bulkApplying}
                  >
                    {bulkApplying ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              </div>
            )}

            <p className="admin-feedback-count">Showing {displayFeedback.length} of {total}{searchTerm.trim() ? ` (filtered)` : ''}</p>

            <div className="admin-feedback-table-wrap">
              <table className="admin-feedback-table">
                <thead>
                  <tr>
                    <th className="admin-feedback-th-check">
                      <input
                        type="checkbox"
                        checked={allSelectedFiltered}
                        onChange={toggleSelectAll}
                        className="admin-feedback-checkbox"
                        aria-label="Select all"
                      />
                    </th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('id')}>
                      <span>#</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'id' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('title')}>
                      <span>Title</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'title' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('submitter')}>
                      <span>Submitter</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'submitter' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('email')}>
                      <span>Email</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'email' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('date')}>
                      <span>Date</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'date' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('message')}>
                      <span>Message</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'message' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th>Imgs</th>
                    <th className="admin-feedback-th-sortable" onClick={() => toggleSort('status')}>
                      <span>Status</span>
                      <span className="admin-feedback-sort-icon">{sortBy === 'status' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayFeedback.map((item) => (
                    <tr
                      key={item.id}
                      className={`admin-feedback-row-clickable ${selectedIds.has(item.id) ? 'selected' : ''}`}
                      onClick={(e) => handleRowClick(e, item)}
                    >
                      <td className="admin-feedback-td-check" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="admin-feedback-checkbox"
                          aria-label={`Select feedback ${item.id}`}
                        />
                      </td>
                      <td className="admin-feedback-td-id">#{item.id}</td>
                      <td className="admin-feedback-td-title">
                        {item.title ? truncate(item.title, 40) : '—'}
                      </td>
                      <td className="admin-feedback-td-submitter">{item.user_name || item.user_email || '—'}</td>
                      <td className="admin-feedback-td-email">{item.user_email}</td>
                      <td className="admin-feedback-td-date">
                        <span className="admin-feedback-date-line">{new Date(item.created_at).toLocaleDateString()}</span>
                        <span className="admin-feedback-time-line">{new Date(item.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                      </td>
                      <td className="admin-feedback-td-message" title="Click row for full message">
                        <span className="admin-feedback-message-preview">{truncate(item.message, MESSAGE_PREVIEW_LEN) || '—'}</span>
                      </td>
                      <td className="admin-feedback-td-imgs">
                        {Array.isArray(item.image_data)
                          ? item.image_data.length
                          : Array.isArray(item.image_names)
                            ? item.image_names.length
                            : 0}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <select
                          value={statusUpdates[item.id] ?? item.status ?? 'active'}
                          onChange={(e) => setStatusUpdates((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          className="admin-feedback-status-select"
                          style={{ borderColor: getStatusColor(statusUpdates[item.id] ?? item.status ?? 'active') }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="admin-feedback-save-btn"
                          onClick={() => handleSave(item.id)}
                          disabled={saving === item.id}
                        >
                          {saving === item.id ? '…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {detailItem && (
        <div className="admin-feedback-modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="admin-feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-feedback-modal-header">
              <h2>Feedback #{detailItem.id}</h2>
              <button className="admin-feedback-modal-close" onClick={() => setDetailItem(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="admin-feedback-modal-body">
              <div className="admin-feedback-modal-field">
                <label>Title</label>
                <p>{detailItem.title || '—'}</p>
              </div>
              <div className="admin-feedback-modal-field">
                <label>Submitter</label>
                <p>{detailItem.user_name || detailItem.user_email || '—'}</p>
              </div>
              <div className="admin-feedback-modal-field">
                <label>Email</label>
                <p>{detailItem.user_email}</p>
              </div>
              <div className="admin-feedback-modal-field">
                <label>Date</label>
                <p>{new Date(detailItem.created_at).toLocaleString()}</p>
              </div>
              <div className="admin-feedback-modal-field">
                <label>Message</label>
                <pre className="admin-feedback-modal-message">{detailItem.message || '—'}</pre>
              </div>
              {(Array.isArray(detailItem.image_data) && detailItem.image_data.length > 0) && (
                <div className="admin-feedback-modal-field">
                  <label>Images</label>
                  <div className="admin-feedback-modal-images">
                    {detailItem.image_data.map((img, idx) => {
                      const src = `data:${img.type || 'image/png'};base64,${img.content}`;
                      const filename = img.filename || `image-${idx + 1}.png`;
                      return (
                        <div key={idx} className="admin-feedback-modal-image-wrap">
                          <div className="admin-feedback-modal-img-container">
                            <img
                              src={src}
                              alt={filename}
                              className="admin-feedback-modal-img admin-feedback-modal-img-clickable"
                              onClick={() => setExpandedImage({ src, filename })}
                              title="Click to expand"
                            />
                            <div className="admin-feedback-modal-img-actions">
                              <button
                                type="button"
                                className="admin-feedback-img-btn"
                                onClick={() => setExpandedImage({ src, filename })}
                              >
                                Expand
                              </button>
                              <a
                                href={src}
                                download={filename}
                                className="admin-feedback-img-btn admin-feedback-img-btn-download"
                              >
                                Download
                              </a>
                            </div>
                          </div>
                          <span className="admin-feedback-modal-img-name">{filename}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {(!detailItem.image_data?.length && Array.isArray(detailItem.image_names) && detailItem.image_names.length > 0) && (
                <div className="admin-feedback-modal-field">
                  <label>Attachments</label>
                  <p className="admin-feedback-modal-attachments">{(detailItem.image_names || []).join(', ')} (see email for images)</p>
                </div>
              )}
              <div className="admin-feedback-modal-field admin-feedback-modal-edit">
                <label>Status</label>
                <select
                  value={statusUpdates[detailItem.id] ?? detailItem.status ?? 'active'}
                  onChange={(e) => setStatusUpdates((prev) => ({ ...prev, [detailItem.id]: e.target.value }))}
                  className="admin-feedback-status-select"
                  style={{ borderColor: getStatusColor(statusUpdates[detailItem.id] ?? detailItem.status ?? 'active') }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="admin-feedback-modal-field admin-feedback-modal-edit">
                <label>Admin Notes</label>
                <textarea
                  value={adminNotes[detailItem.id] ?? detailItem.admin_notes ?? ''}
                  onChange={(e) => setAdminNotes((prev) => ({ ...prev, [detailItem.id]: e.target.value }))}
                  placeholder="Add notes..."
                  rows={3}
                  className="admin-feedback-modal-notes"
                />
              </div>
            </div>
            <div className="admin-feedback-modal-footer">
              <button className="admin-feedback-modal-btn admin-feedback-modal-btn-secondary" onClick={() => setDetailItem(null)}>
                Cancel
              </button>
              <button
                className="admin-feedback-modal-btn admin-feedback-save-btn"
                onClick={async () => {
                  const ok = await handleSave(detailItem.id);
                  if (ok) setDetailItem(null);
                }}
                disabled={saving === detailItem.id}
              >
                {saving === detailItem.id ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {expandedImage && (
        <div
          className="admin-feedback-expanded-overlay"
          onClick={() => setExpandedImage(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setExpandedImage(null)}
          aria-label="Close expanded image"
        >
          <button className="admin-feedback-expanded-close" onClick={() => setExpandedImage(null)} aria-label="Close">
            ×
          </button>
          <img
            src={expandedImage.src}
            alt={expandedImage.filename}
            className="admin-feedback-expanded-img"
            onClick={(e) => e.stopPropagation()}
          />
          <a
            href={expandedImage.src}
            download={expandedImage.filename}
            className="admin-feedback-expanded-download"
            onClick={(e) => e.stopPropagation()}
          >
            Download
          </a>
        </div>
      )}
    </div>
  );
}

export default AdminFeedback;
